import {
  Bell,
  Cart,
  EmoRacha,
  EmoRachaActive,
  Event,
  Foro,
  Gift,
  Home,
  Market,
  Menu,
  Messages,
  Pack,
} from "@assets"
import { Link, router, usePage } from "@inertiajs/react"
import { Briefcase, Mic, Moon, Sun } from "lucide-react"
import { useEffect, useState } from "react"

import { AuthButton } from "@/components/auth-button"
import { Breadcrumbs } from "@/components/breadcrumbs"
import { Icon } from "@/components/icon"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { UserMenuContent } from "@/components/user-menu-content"
import { useInitials } from "@/hooks/use-initials"
import { useNotifications } from "@/hooks/use-notifications"
import { cn } from "@/lib/utils"
import {
  businessOrdersPath,
  cartPath,
  chatGroupsPath,
  eventsPath,
  forumPath,
  interviewsPath,
  jobsPath,
  marketplacePath,
  notificationsPath,
  ordersPath,
  rafflesPath,
  rootPath,
  settingsProfilePath,
} from "@/routes"
import type { BreadcrumbItem, NavItem, SharedData } from "@/types"

import AppLogo from "./app-logo"

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const mainNavItems: NavItem[] = [
  {
    title: "Inicio",
    href: rootPath(),
    icon: Home,
  },
  {
    title: "Eventos",
    href: eventsPath(),
    icon: Event,
  },
  {
    title: "Sorteos",
    href: rafflesPath(),
    icon: Gift,
  },
  {
    title: "Entrevistas",
    href: interviewsPath(),
    icon: Mic,
  },
  {
    title: "Foro",
    href: forumPath(),
    icon: Foro,
  },
  {
    title: "Mercado",
    href: marketplacePath(),
    icon: Market,
  },
  {
    title: "Curros",
    href: jobsPath(),
    icon: Briefcase,
  },
]
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

const activeItemStyles =
  "text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100"

const toRelativePath = (url: string): string => {
  try {
    const parsed = new URL(url)
    return parsed.pathname + parsed.search + parsed.hash
  } catch {
    return url
  }
}

interface AppHeaderProps {
  breadcrumbs?: BreadcrumbItem[]
}

export function AppHeader({ breadcrumbs = [] }: AppHeaderProps) {
  const page = usePage<SharedData>()
  const sharedProps = page.props
  const { auth, cart, app } = sharedProps
  const userId = auth?.user?.id ?? null
  const serverCartCount = cart?.count ?? 0

  const CART_COUNT_KEY = "cart_count"
  const CART_OWNER_KEY = "cart_owner_id"

  const hasProducts = Boolean(app?.has_products)
  const getInitials = useInitials()
  const [cartCount, setCartCount] = useState<number>(serverCartCount)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : true,
  )

  const toggleTheme = () => {
    const dark = document.documentElement.classList.toggle("dark")
    setIsDark(dark)
    localStorage.appearance = dark ? "dark" : "light"
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", dark ? "#121212" : "#ecf4ff")
    document.documentElement.style.background = dark ? "#121212" : "#ecf4ff"
  }
  // Notifications from Inertia shared props
  const recentNotifications: {
    id: number | string
    title?: string | null
    url?: string | null
    created_at?: string
  }[] = sharedProps.auth?.notifications?.recent ?? []
  const { unreadCount, setUnreadCount } = useNotifications()
  const messagesUnreadCount = sharedProps.auth?.messages?.unread_count ?? 0

  const csrfToken = () =>
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""

  const handleNotificationClick = async (
    id: number | string,
    url?: string | null,
  ) => {
    try {
      const res = await fetch(`/notificaciones/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ action_type: "mark_as_read" }),
        credentials: "same-origin",
      })
      const resData = (await res.json()) as { unread_count?: number }

      if (typeof resData.unread_count === "number") {
        setUnreadCount(resData.unread_count)
        window.dispatchEvent(
          new CustomEvent("notifications:updated", {
            detail: { unread_count: resData.unread_count },
          }),
        )
      }

      if (url) {
        router.visit(toRelativePath(url))
      } else {
        router.reload({ only: ["auth"] })
      }
    } catch {
      // silent
    }
  }

  const syncCartStorage = (nextCount: number) => {
    if (typeof window === "undefined") return
    if (userId) {
      sessionStorage.setItem(CART_OWNER_KEY, String(userId))
    } else {
      sessionStorage.removeItem(CART_OWNER_KEY)
    }
    sessionStorage.setItem(CART_COUNT_KEY, String(nextCount))
  }

  useEffect(() => {
    if (typeof window === "undefined") return
    const storedOwner = sessionStorage.getItem(CART_OWNER_KEY)
    const storedCount = sessionStorage.getItem(CART_COUNT_KEY)

    if (!userId) {
      setCartCount(serverCartCount)
      syncCartStorage(serverCartCount)
      return
    }

    if (storedOwner !== String(userId)) {
      setCartCount(serverCartCount)
      syncCartStorage(serverCartCount)
      return
    }

    if (storedCount !== null) {
      const parsed = parseInt(storedCount, 10)
      if (!Number.isNaN(parsed)) {
        if (parsed !== serverCartCount) {
          setCartCount(serverCartCount)
          syncCartStorage(serverCartCount)
        } else {
          setCartCount(parsed)
        }
        return
      }
    }

    setCartCount(serverCartCount)
    syncCartStorage(serverCartCount)
  }, [userId, serverCartCount])

  // Listen for real-time cart updates from add/remove cart actions
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ cart_count?: number }>
      if (typeof ce.detail?.cart_count === "number") {
        setCartCount(ce.detail.cart_count)
        syncCartStorage(ce.detail.cart_count)
      }
    }
    window.addEventListener("cart:updated", handler)
    return () => {
      window.removeEventListener("cart:updated", handler)
    }
  }, [userId])

  return (
    <>
      <div className="border-sidebar-border/80 border-b">
        <div className="mx-auto flex h-16 items-center px-4 md:max-w-7xl">
          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="mt-2 mr-2 h-[34px] w-[34px]"
                >
                  <Menu className="text-primary size-6" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="bg-sidebar flex h-full w-64 flex-col items-stretch justify-between"
              >
                <SheetTitle className="sr-only">Menú de Navegación</SheetTitle>
                <SheetHeader className="flex justify-start text-left"></SheetHeader>
                <div className="flex h-full flex-1 flex-col space-y-4 p-4">
                  <div className="flex h-full flex-col justify-between text-sm">
                    <div className="flex flex-col space-y-4">
                      {mainNavItems.map((item) => (
                        <Link
                          key={item.title}
                          href={item.href}
                          prefetch
                          className="flex items-center space-x-2 font-medium"
                        >
                          {item.icon && typeof item.icon !== "string" && (
                            <Icon iconNode={item.icon} className="h-5 w-5" />
                          )}
                          <span>{item.title}</span>
                        </Link>
                      ))}
                      {auth?.user && (
                        <>
                          <div className="text-muted-foreground pt-2 text-xs">
                            Tu cuenta
                          </div>
                          <Link
                            href={ordersPath()}
                            prefetch="hover"
                            className="flex items-center space-x-2 font-medium"
                          >
                            <Pack className="h-5 w-5" />
                            <span>Mis Pedidos</span>
                          </Link>
                          {auth.user.has_business && (
                            <Link
                              href={businessOrdersPath()}
                              prefetch="hover"
                              className="flex items-center space-x-2 font-medium"
                            >
                              <Pack className="h-5 w-5" />
                              <span>Pedidos de mi negocio</span>
                            </Link>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <Link
            href={rootPath()}
            prefetch="mount"
            className="flex items-center space-x-2"
          >
            <AppLogo />
          </Link>

          {/* Desktop Navigation */}
          <div className="ml-6 hidden h-full items-center space-x-6 lg:flex">
            <NavigationMenu className="flex h-full items-stretch">
              <NavigationMenuList className="flex h-full items-stretch space-x-2">
                {mainNavItems.map((item, index) => (
                  <NavigationMenuItem
                    key={index}
                    className="relative flex h-full items-center"
                  >
                    <Link
                      href={item.href}
                      prefetch
                      className={cn(
                        navigationMenuTriggerStyle(),
                        page.url === item.href && activeItemStyles,
                        "h-9 cursor-pointer px-3",
                      )}
                    >
                      {item.icon && typeof item.icon !== "string" && (
                        <Icon
                          iconNode={item.icon}
                          className="mr-2 h-4 w-4"
                        />
                      )}
                      {item.title}
                    </Link>
                    {page.url === item.href && (
                      <div className="absolute bottom-0 left-0 h-0.5 w-full translate-y-px bg-black dark:bg-white"></div>
                    )}
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="ml-auto flex items-center space-x-2">
            <div className="relative flex items-center space-x-1">
              {hasProducts && cartCount > 0 && (
                <Link href={cartPath()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="group relative h-9 w-9 cursor-pointer"
                  >
                    <Cart className="!size-5 fill-current opacity-80 group-hover:opacity-100" />
                    {cartCount > 0 && (
                      <span className="bg-primary text-primary-foreground absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px]">
                        {cartCount}
                      </span>
                    )}
                  </Button>
                </Link>
              )}
              {auth?.user && (
                <Link href={chatGroupsPath()} prefetch="hover">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="group relative h-9 w-9 cursor-pointer"
                    aria-label="Mensajes"
                  >
                    <Messages className="!size-5 fill-current opacity-80 group-hover:opacity-100" />
                    {messagesUnreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] text-white">
                        {messagesUnreadCount}
                      </span>
                    )}
                  </Button>
                </Link>
              )}
              {auth?.user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="group relative h-9 w-9 cursor-pointer"
                      aria-label="Notificaciones"
                    >
                      <Bell className="!size-5 fill-current opacity-80 group-hover:opacity-100" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] text-white">
                          {unreadCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-80 overflow-hidden p-0"
                  >
                    <div className="border-b px-3 py-2 text-sm font-medium">
                      Notificaciones
                    </div>
                    <div className="max-h-80 overflow-auto">
                      {recentNotifications.length === 0 ? (
                        <div className="text-muted-foreground px-3 py-6 text-center text-sm">
                          No tienes notificaciones
                        </div>
                      ) : (
                        recentNotifications.map((n) => (
                          <DropdownMenuItem
                            key={n.id}
                            className="cursor-pointer whitespace-normal"
                            onClick={() => {
                              void handleNotificationClick(n.id, n.url)
                            }}
                          >
                            <div className="flex w-full flex-col gap-0.5 py-2">
                              <span className="text-sm leading-snug">
                                {n.title ?? "Notificación"}
                              </span>
                              {n.created_at && (
                                <span className="text-muted-foreground text-xs">
                                  {n.created_at}
                                </span>
                              )}
                            </div>
                          </DropdownMenuItem>
                        ))
                      )}
                    </div>
                    <div className="border-t p-2">
                      <Link
                        href={notificationsPath()}
                        prefetch="hover"
                        className="block w-full"
                      >
                        <Button variant="ghost" className="w-full">
                          Ver todas
                        </Button>
                      </Link>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {/* Streak indicator with badge */}
            {auth?.user && (
              <div className="relative hidden h-9 w-9 items-center justify-center md:inline-flex">
                <img
                  src={
                    (auth.user.streak_count ?? 0) > 0
                      ? EmoRachaActive
                      : EmoRacha
                  }
                  className="h-6 w-6"
                  alt="Racha"
                />
                {(auth.user.streak_count ?? 0) > 0 && (
                  <span className="bg-secondary absolute -top-1 -right-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white">
                    {(auth.user.streak_count ?? 0) > 9
                      ? "9+"
                      : auth.user.streak_count}
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={toggleTheme}
              className="text-muted-foreground hover:text-foreground hidden h-9 w-9 rounded-md p-1.5 md:inline-flex md:items-center md:justify-center"
              aria-label={isDark ? "Modo claro" : "Modo oscuro"}
            >
              {isDark ? (
                <Sun className="h-full w-full" />
              ) : (
                <Moon className="h-full w-full" />
              )}
            </button>
            {auth?.user ? (
              <Link
                href={settingsProfilePath()}
                prefetch
                className="hidden text-sm font-medium md:inline-block"
              >
                {auth.user.name}
              </Link>
            ) : (
              <AuthButton variant="ghost">Iniciar sesión</AuthButton>
            )}
            {auth?.user && (
              <DropdownMenu
                open={isDropdownOpen}
                onOpenChange={setIsDropdownOpen}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className={`h-[48px] w-[48px] rounded-full bg-transparent p-0 focus:ring-0 focus:outline-none focus-visible:border-0 focus-visible:ring-0 ${isDropdownOpen ? "bg-primary" : ""}`}
                  >
                    <Avatar
                      className={`gradient-avatar h-[42px] w-[42px] border-[2px] ${isDropdownOpen ? "border-white" : "border-chart-2"}`}
                    >
                      <AvatarImage
                        src={auth.user.avatar_url ?? undefined}
                        alt={auth.user.name}
                      />
                      <AvatarFallback className="text-primary gradient-avatar p-1 text-[20px] font-semibold tracking-[0.2px]">
                        {getInitials(auth.user.name ?? "")}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <UserMenuContent auth={auth} />
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
      {breadcrumbs.length > 1 && (
        <div className="border-sidebar-border/70 flex w-full border-b">
          <div className="mx-auto flex h-12 w-full items-center justify-start px-4 text-neutral-500 md:max-w-7xl">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>
        </div>
      )}
    </>
  )
}
